// File: src/hooks/useScrollProgress.js
import { useState, useEffect } from 'react';

/**
 * Shared, ref-counted scroll-listener singleton.
 *
 * Every section that needs its own parallax "scroll progress" value used to
 * register its own native `scroll` listener (6 listeners total, each
 * triggering its own React state update on every scroll event). Instead,
 * we register exactly one native `scroll` listener for the whole app, and
 * fan the event out to every subscriber from inside a single
 * requestAnimationFrame-throttled callback.
 */
const subscribers = new Set();
let rafId = null;
let isListening = false;

const notifySubscribers = () => {
  rafId = null;
  subscribers.forEach((callback) => callback());
};

const handleWindowScroll = () => {
  // rAF-throttle: only ever have one pending frame at a time.
  if (rafId === null) {
    rafId = requestAnimationFrame(notifySubscribers);
  }
};

const subscribe = (callback) => {
  subscribers.add(callback);

  if (!isListening) {
    window.addEventListener('scroll', handleWindowScroll);
    isListening = true;
  }

  return () => {
    subscribers.delete(callback);

    if (subscribers.size === 0 && isListening) {
      window.removeEventListener('scroll', handleWindowScroll);
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
      isListening = false;
    }
  };
};

/**
 * Tracks a 0-100 scroll-progress value for the section attached to `ref`,
 * driven by the shared scroll-listener singleton above instead of each
 * section registering its own listener.
 *
 * @param {React.RefObject} ref - ref already attached to the section
 *   element whose parallax progress should be tracked.
 * @returns {number} scroll progress on a 0-100 scale (matches the previous
 *   per-section implementation), or 0 if the user prefers reduced motion.
 */
const useScrollProgress = (ref) => {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const prefersReducedMotion =
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      return undefined;
    }

    const handleScroll = () => {
      if (ref.current) {
        const rect = ref.current.getBoundingClientRect();
        const sectionTop = rect.top;
        const sectionHeight = rect.height;
        const windowHeight = window.innerHeight;

        // Calculate parallax effect only when section is visible
        if (sectionTop < windowHeight && sectionTop + sectionHeight > 0) {
          // Calculate the scroll progress through this section
          const scrollProgress = (windowHeight - sectionTop) / (windowHeight + sectionHeight);
          setScrollY(scrollProgress * 100); // Adjust multiplier for effect intensity
        }
      }
    };

    const unsubscribe = subscribe(handleScroll);
    handleScroll(); // Initial call

    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return scrollY;
};

export { subscribe };
export default useScrollProgress;
